import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './CurrencyConverter.css';

function CurrencyConverter() {
  const [amount, setAmount] = useState('1');
  const [fromCurrency, setFromCurrency] = useState('usd');
  const [toCurrency, setToCurrency] = useState('eur');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [convertedAmount, setConvertedAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currencies, setCurrencies] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [conversionHistory, setConversionHistory] = useState([]);

  // Popular currencies for quick selection
  const popularCurrencies = [
    { code: 'usd', name: 'US Dollar', symbol: '$' },
    { code: 'eur', name: 'Euro', symbol: '€' },
    { code: 'gbp', name: 'British Pound', symbol: '£' },
    { code: 'jpy', name: 'Japanese Yen', symbol: '¥' },
    { code: 'cad', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'aud', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'chf', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'cny', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'inr', name: 'Indian Rupee', symbol: '₹' },
    { code: 'krw', name: 'South Korean Won', symbol: '₩' },
    { code: 'myr', name: 'Malaysian Ringgit', symbol: 'RM' },
    { code: 'sgd', name: 'Singapore Dollar', symbol: 'S$' }
  ];

  // Fetch available currencies on component mount
  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const primaryUrl = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies.json';
      const fallbackUrl = 'https://latest.currency-api.pages.dev/v1/currencies.json';
      
      let response;
      try {
        response = await fetch(primaryUrl);
        if (!response.ok) throw new Error('Primary API failed');
      } catch (error) {
        console.log('Primary API failed, trying fallback...');
        response = await fetch(fallbackUrl);
        if (!response.ok) throw new Error('Both APIs failed');
      }
      
      const data = await response.json();
      setCurrencies(data);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setError('Failed to load currencies. Please try again later.');
    }
  };

  const convertCurrency = useCallback(async () => {
    if (!amount || amount <= 0 || fromCurrency === toCurrency) return;

    setLoading(true);
    setError(null);

    try {
      const primaryUrl = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${fromCurrency}.json`;
      const fallbackUrl = `https://latest.currency-api.pages.dev/v1/currencies/${fromCurrency}.json`;
      
      let response;
      try {
        response = await fetch(primaryUrl);
        if (!response.ok) throw new Error('Primary API failed');
      } catch (error) {
        console.log('Primary API failed, trying fallback...');
        response = await fetch(fallbackUrl);
        if (!response.ok) throw new Error('Both APIs failed');
      }

      const data = await response.json();
      
      if (data[fromCurrency] && data[fromCurrency][toCurrency] !== undefined) {
        const rate = data[fromCurrency][toCurrency];
        const result = (parseFloat(amount) * rate).toFixed(4);
        
        setExchangeRate(rate);
        setConvertedAmount(result);
        setLastUpdated(data.date);
        
        // Add to conversion history
        const historyEntry = {
          id: Date.now(),
          amount: parseFloat(amount),
          from: fromCurrency.toUpperCase(),
          to: toCurrency.toUpperCase(),
          rate: rate,
          result: result,
          timestamp: new Date().toLocaleString()
        };
        
        setConversionHistory(prev => [historyEntry, ...prev.slice(0, 9)]); // Keep last 10 conversions
      } else {
        throw new Error(`Exchange rate not found for ${fromCurrency.toUpperCase()} to ${toCurrency.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Error converting currency:', error);
      setError('Failed to fetch exchange rate. Please try again.');
      setExchangeRate(null);
      setConvertedAmount(null);
    } finally {
      setLoading(false);
    }
  }, [amount, fromCurrency, toCurrency]);

  // Convert currency when inputs change
  useEffect(() => {
    if (amount && fromCurrency && toCurrency && fromCurrency !== toCurrency) {
      convertCurrency();
    }
  }, [amount, fromCurrency, toCurrency, convertCurrency]);

  const swapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const formatCurrency = (amount, currencyCode) => {
    const popular = popularCurrencies.find(c => c.code === currencyCode);
    if (popular && popular.symbol) {
      return `${popular.symbol}${amount}`;
    }
    return `${amount} ${currencyCode.toUpperCase()}`;
  };

  const getCurrencyName = (code) => {
    return currencies[code] || code.toUpperCase();
  };

  const clearHistory = () => {
    setConversionHistory([]);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Currency Converter - Live Exchange Rates",
    "description": "Free currency converter with live exchange rates for 200+ currencies including USD, EUR, GBP, JPY, MYR, SGD, and cryptocurrencies. Real-time currency exchange calculator.",
    "url": "https://infi-tools.com/currency-converter",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "200+ currencies supported",
      "Live exchange rates",
      "Major world currencies (USD, EUR, GBP, JPY)",
      "Southeast Asian currencies (MYR, SGD)",
      "Cryptocurrency support",
      "Conversion history",
      "Quick currency selection",
      "Real-time rate updates"
    ]
  };

  return (
    <div className="currency-converter-page">
      <SEO 
        title="Currency Converter - Live Exchange Rates for 200+ Currencies"
        description="Free currency converter with live exchange rates for 200+ currencies including USD, EUR, GBP, JPY, MYR, SGD, and cryptocurrencies. Convert any currency with real-time rates."
        keywords="currency converter, exchange rates, USD EUR, currency exchange, live rates, MYR SGD, cryptocurrency converter, foreign exchange, forex converter, currency calculator"
        canonicalUrl="https://infi-tools.com/currency-converter"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Currency Converter</h1>
          <p className="page-description">
            Convert between 200+ currencies with live exchange rates. Includes major world currencies and popular cryptocurrencies.
          </p>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="5164626294" style={{ marginBottom: '20px' }} />

        <div className="currency-converter-container">
          <div className="converter-section">
            <div className="conversion-card">
              <h3>Currency Conversion</h3>
              
              <div className="amount-input-group">
                <label htmlFor="amount">Amount:</label>
                <input
                  type="text"
                  id="amount"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="Enter amount"
                  className="amount-input"
                />
              </div>

              <div className="currency-selection">
                <div className="currency-group">
                  <label htmlFor="from-currency">From:</label>
                  <select
                    id="from-currency"
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    className="currency-select"
                  >
                    <optgroup label="Popular Currencies">
                      {popularCurrencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code.toUpperCase()} - {currency.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="All Currencies">
                      {Object.entries(currencies).map(([code, name]) => (
                        <option key={code} value={code}>
                          {code.toUpperCase()} - {name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <button className="swap-btn" onClick={swapCurrencies} title="Swap currencies">
                  ⇄
                </button>

                <div className="currency-group">
                  <label htmlFor="to-currency">To:</label>
                  <select
                    id="to-currency"
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    className="currency-select"
                  >
                    <optgroup label="Popular Currencies">
                      {popularCurrencies.map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code.toUpperCase()} - {currency.name}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="All Currencies">
                      {Object.entries(currencies).map(([code, name]) => (
                        <option key={code} value={code}>
                          {code.toUpperCase()} - {name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {loading && (
                <div className="loading-indicator">
                  <div className="spinner"></div>
                  <span>Converting...</span>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {error}
                </div>
              )}

              {convertedAmount && exchangeRate && !loading && !error && (
                <div className="conversion-result">
                  <div className="result-display">
                    <div className="from-amount">
                      {formatCurrency(amount, fromCurrency)}
                    </div>
                    <div className="equals">=</div>
                    <div className="to-amount">
                      {formatCurrency(convertedAmount, toCurrency)}
                    </div>
                  </div>
                  
                  <div className="exchange-rate-info">
                    <p>1 {fromCurrency.toUpperCase()} = {exchangeRate.toFixed(6)} {toCurrency.toUpperCase()}</p>
                    {lastUpdated && (
                      <p className="last-updated">Last updated: {lastUpdated}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="popular-currencies-grid">
              <h3>Quick Select</h3>
              <div className="currency-buttons">
                {popularCurrencies.map(currency => (
                  <button
                    key={currency.code}
                    className={`currency-btn ${fromCurrency === currency.code ? 'active' : ''}`}
                    onClick={() => setFromCurrency(currency.code)}
                  >
                    <span className="currency-symbol">{currency.symbol}</span>
                    <span className="currency-code">{currency.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="info-section">
            <div className="currency-info">
              <h3>Currency Information</h3>
              <div className="currency-details">
                <div className="currency-detail">
                  <h4>{fromCurrency.toUpperCase()}</h4>
                  <p>{getCurrencyName(fromCurrency)}</p>
                </div>
                <div className="currency-detail">
                  <h4>{toCurrency.toUpperCase()}</h4>
                  <p>{getCurrencyName(toCurrency)}</p>
                </div>
              </div>
            </div>

            {conversionHistory.length > 0 && (
              <div className="conversion-history">
                <div className="history-header">
                  <h3>Recent Conversions</h3>
                  <button className="clear-history-btn" onClick={clearHistory}>
                    Clear
                  </button>
                </div>
                <div className="history-list">
                  {conversionHistory.map(entry => (
                    <div key={entry.id} className="history-item">
                      <div className="history-conversion">
                        <span className="history-amount">
                          {formatCurrency(entry.amount, entry.from.toLowerCase())}
                        </span>
                        <span className="history-arrow">→</span>
                        <span className="history-result">
                          {formatCurrency(entry.result, entry.to.toLowerCase())}
                        </span>
                      </div>
                      <div className="history-rate">
                        Rate: {entry.rate.toFixed(6)}
                      </div>
                      <div className="history-time">
                        {entry.timestamp}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="api-info">
              <h3>About This Service</h3>
              <ul>
                <li><strong>200+ Currencies:</strong> Major world currencies and cryptocurrencies</li>
                <li><strong>Real-time Rates:</strong> Daily updated exchange rates</li>
                <li><strong>Free Service:</strong> No rate limits or API keys required</li>
                <li><strong>Reliable:</strong> Multiple API endpoints for maximum uptime</li>
              </ul>
              <p className="api-disclaimer">
                <small>
                  Exchange rates are for informational purposes only and may not reflect real-time market rates. 
                  Always verify rates with official financial institutions for actual transactions.
                </small>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="4621700810" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default CurrencyConverter;